import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Card, Empty, PriorityPill, UnitBadge } from '../components/ui';
import { useFlash, useNow, useRealtime, useResource } from '../state/hooks';
import { api } from '../lib/api';
import { clock, elapsed, titleCase } from '../lib/format';
import type { FieldMessage, Unit, UnitStatus } from '../lib/types';
import { useAuth } from '../state/AuthContext';

/**
 * Field view, for the crew rather than the control room.
 *
 * Built for a phone held in one hand at the roadside: one column, large
 * targets, and the four status changes a crew actually makes reachable without
 * scrolling. Position is only ever sent when the crew asks for it — a dispatch
 * app that quietly tracks its users all shift is a different product, and not
 * one anyone consented to.
 */

const CREW_STATUSES: { status: UnitStatus; label: string }[] = [
  { status: 'en_route', label: 'En route' },
  { status: 'on_scene', label: 'On scene' },
  { status: 'available', label: 'Clear / available' },
  { status: 'busy', label: 'Busy' },
];

export function FieldConsole() {
  const { user, can } = useAuth();
  const now = useNow();
  const [flash, showFlash] = useFlash();
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);

  const units = useResource<{ units: Unit[] }>('/units');
  const messages = useResource<{ messages: FieldMessage[]; unacknowledged: number }>('/messages?limit=40');

  const refresh = useCallback(() => {
    units.reload();
    messages.reload();
  }, [units, messages]);
  useRealtime(['units', 'messages', 'incidents'], refresh, refresh);

  const myUnits = useMemo(
    () => (units.data?.units ?? []).filter((unit) => unit.operator_id === user?.id),
    [units.data, user?.id],
  );

  // A crew member sees their own unit. Somebody with the roster permission but
  // no unit of their own can pick one, which is how a supervisor covers a crew
  // whose handset has died.
  const unit =
    myUnits.find((candidate) => candidate.id === selectedUnitId) ??
    myUnits[0] ??
    (can('dispatch.assign')
      ? (units.data?.units ?? []).find((candidate) => candidate.id === selectedUnitId) ?? null
      : null);

  const act = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      showFlash('ok', label);
      refresh();
    } catch (err) {
      showFlash('error', (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reportPosition = () => {
    if (!unit) return;
    if (!navigator.geolocation) {
      showFlash('error', 'This device cannot report a position.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        void act('Position sent', () =>
          api.post(`/units/${unit.id}/location`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        );
      },
      (error) => showFlash('error', `Could not read a position: ${error.message}`),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  if (!unit) {
    return (
      <div className="mobile stack">
        <Card title="Field">
          <Empty>
            No unit is signed on to this account.
            {can('units.manage') && ' An administrator can set you as a unit operator in Admin → Units.'}
          </Empty>
          {can('dispatch.assign') && (units.data?.units.length ?? 0) > 0 && (
            <select value={selectedUnitId ?? ''} onChange={(event) => setSelectedUnitId(event.target.value || null)}>
              <option value="">Cover a unit…</option>
              {(units.data?.units ?? []).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.call_sign} — {candidate.name}
                </option>
              ))}
            </select>
          )}
        </Card>
      </div>
    );
  }

  const assignment = unit.assignments[0] ?? null;
  const unitMessages = (messages.data?.messages ?? []).filter((message) => message.unit_id === unit.id);

  return (
    <div className="mobile stack">
      {flash && <Banner kind={flash.kind}>{flash.message}</Banner>}

      <Card
        title={
          <div className="row">
            <h1>{unit.call_sign}</h1>
            <UnitBadge status={unit.status} />
          </div>
        }
        actions={<span className="faint">{elapsed(unit.status_updated_at, now)}</span>}
      >
        <div className="stack">
          <div className="faint">{unit.name}{unit.operator_name ? ` · ${unit.operator_name}` : ''}</div>

          <div className="status-grid">
            {CREW_STATUSES.map((option) => (
              <button
                key={option.status}
                className={`btn${unit.status === option.status ? ' primary' : ''}`}
                disabled={busy || unit.status === option.status}
                onClick={() =>
                  void act(`Status set to ${option.label.toLowerCase()}`, () =>
                    api.post(`/units/${unit.id}/status`, {
                      status: option.status,
                      incidentId: assignment?.incidentId ?? null,
                    }),
                  )
                }
              >
                {option.label}
              </button>
            ))}
          </div>

          <button className="btn block" disabled={busy} onClick={reportPosition}>
            Send my position
          </button>
          {unit.location_updated_at && (
            <div className="faint">Last position {elapsed(unit.location_updated_at, now)} ago</div>
          )}
        </div>
      </Card>

      <Card title="Current assignment">
        {!assignment ? (
          <Empty>Nothing assigned. Stand by.</Empty>
        ) : (
          <div className="stack tight">
            <div className="row">
              <PriorityPill code={null} color={assignment.priorityColor} level={assignment.priorityLevel} />
              <Link to={`/incidents/${assignment.incidentId}`} className="mono">{assignment.incidentNo}</Link>
              <span className="badge accent">{titleCase(assignment.state)}</span>
            </div>
            <div>{assignment.address ?? 'No address given'}</div>
            <div className="faint">Assigned {elapsed(assignment.assignedAt, now)} ago</div>
          </div>
        )}
      </Card>

      <Card title="Messages" flush>
        <ul className="list scroll-md">
          {unitMessages.length === 0 && <li><Empty>No messages.</Empty></li>}
          {unitMessages.map((message) => (
            <li key={message.id}>
              <div className="item" style={{ cursor: 'default' }}>
                <span className="grow">
                  <span className="item-title">
                    {message.direction === 'outbound' ? 'Control' : unit.call_sign}
                    {message.incident_no && <span className="badge neutral mono">{message.incident_no}</span>}
                  </span>
                  <span className="item-sub" style={{ whiteSpace: 'pre-wrap' }}>{message.body}</span>
                </span>
                <span className="faint">{clock(message.created_at)}</span>
              </div>
            </li>
          ))}
        </ul>

        <form
          className="row"
          style={{ padding: 12, borderTop: '1px solid var(--border)' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (!draft.trim()) return;
            void act('Message sent', async () => {
              await api.post('/messages/inbound', {
                unitId: unit.id,
                incidentId: assignment?.incidentId ?? null,
                body: draft.trim(),
              });
              setDraft('');
            });
          }}
        >
          <input
            type="text"
            placeholder="Message control…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn primary" type="submit" disabled={busy || !draft.trim()}>
            Send
          </button>
        </form>
      </Card>
    </div>
  );
}
